import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import {
  DEFAULT_HARBOUR_FILTERS,
  HARBOUR_COLUMN,
  HARBOUR_DEFAULT_THRIVING,
  HARBOUR_INTRO_COPY,
  HARBOUR_PAGE_SUBTITLE,
  HARBOUR_PAGE_TITLE,
  HARBOUR_SEARCH_MISS_ACTION,
  HARBOUR_SEARCH_MISS_COPY,
  getHarbourSpartacusHandoffQuery,
} from 'src/constants/harbour'
import {
  applyHarbourDeepLinkOwnerView,
  loadHarbourFilterPrefs,
  mergeHarbourFilterPrefs,
  saveHarbourFilterPrefs,
} from 'src/utils/harbourFilterPrefs'
import { startHarbourWork } from 'src/utils/harbourApi'
import { getHarbourStartWorkSkip } from 'src/utils/harbourCard'
import { getHarbourSoleSearchHit } from 'src/utils/harbourSearch'
import {
  toastHarbourMutationError,
  toastHarbourStartWorkSkip,
  toastHarbourStartWorkSuccess,
} from 'src/utils/harbourToast'
import HarbourBoard from './HarbourBoard'
import HarbourDrawer from './HarbourDrawer'
import HarbourFilters from './HarbourFilters'
import HarbourHowItWorks from './HarbourHowItWorks'
import HarbourNewsletterReminder from './HarbourNewsletterReminder'
import HarbourPulse from './HarbourPulse'
import HarbourToday from './HarbourToday'
import { useHarbourNeedsYou, useHarbourWork } from './useHarbourBoard'
import { useHarbourPulse } from './useHarbourPulse'
import { useHarbourSearch } from './useHarbourSearch'

/**
 * Harbour panel — CS work queue on The Bridge.
 * Pulse + compact How-it-works + a short empty Today so Needs you
 * stays in the first viewport. Never wait on Needs you for Today.
 */
const Harbour = ({
  isActive = true,
  onOpenSpartacus,
  deepLinkVenueId = null,
  onDeepLinkVenueChange,
} = {}) => {
  const account = useSelector(state => state.auth?.account)
  const accountId = account?._id || null
  const [filters, setFilters] = useState(DEFAULT_HARBOUR_FILTERS)
  const [showThriving, setShowThriving] = useState(HARBOUR_DEFAULT_THRIVING)
  const [drawerVenue, setDrawerVenue] = useState(null)
  const [startBusyVenueId, setStartBusyVenueId] = useState(null)
  const hydratedAccountIdRef = useRef(null)

  const applyStickyFilters = useCallback(
    updater => {
      setFilters(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        if (accountId) {
          saveHarbourFilterPrefs(accountId, next)
        }
        return next
      })
    },
    [accountId],
  )

  const search = useHarbourSearch({ enabled: isActive })
  const autoOpenedSearchRef = useRef('')

  useEffect(() => {
    if (!accountId) {
      return undefined
    }
    if (hydratedAccountIdRef.current === String(accountId)) {
      return undefined
    }
    hydratedAccountIdRef.current = String(accountId)
    const prefs = loadHarbourFilterPrefs(accountId)
    setFilters(prev => {
      const merged = prefs ? mergeHarbourFilterPrefs(prev, prefs) : prev
      const next = applyHarbourDeepLinkOwnerView(merged, deepLinkVenueId)
      if (next.ownerFilter !== merged.ownerFilter) {
        saveHarbourFilterPrefs(accountId, next)
      }
      return next
    })
    if (prefs) {
      search.setQuery('')
      search.setGap('')
    }
    return undefined
  }, [accountId, deepLinkVenueId, search.setQuery])

  useEffect(() => {
    if (!isActive || !deepLinkVenueId) {
      return undefined
    }
    setDrawerVenue(prev =>
      prev?.venueId === deepLinkVenueId ? prev : { venueId: deepLinkVenueId },
    )
    setFilters(prev => {
      const next = applyHarbourDeepLinkOwnerView(prev, deepLinkVenueId)
      if (next.ownerFilter !== prev.ownerFilter && accountId) {
        saveHarbourFilterPrefs(accountId, next)
      }
      return next
    })
    return undefined
  }, [deepLinkVenueId, isActive, accountId])

  useEffect(() => {
    if (isActive) {
      return undefined
    }
    setDrawerVenue(null)
    return undefined
  }, [isActive])

  const boardFilters = {
    ownerFilter: filters.ownerFilter,
    planFilter: filters.planFilter,
    hideParked: filters.hideParked,
    hasPhone: filters.hasPhone,
  }

  const {
    pulse,
    loading: pulseLoading,
    error: pulseError,
    refresh: refreshPulse,
  } = useHarbourPulse({
    planFilter: filters.planFilter,
    enabled: isActive,
  })

  const {
    board: workBoard,
    loading: workLoading,
    error: workError,
    refresh: refreshWork,
  } = useHarbourWork({
    ...boardFilters,
    enabled: isActive,
  })

  const {
    board: needsYouBoard,
    loading: needsYouLoading,
    error: needsYouError,
    refresh: refreshNeedsYou,
  } = useHarbourNeedsYou({
    ...boardFilters,
    thriving: showThriving,
    enabled: isActive,
  })

  useEffect(() => {
    if (needsYouBoard?.thriving?.available === false) {
      setShowThriving(false)
    }
  }, [needsYouBoard?.thriving?.available])

  const refreshBoards = useCallback(() => {
    refreshWork()
    refreshNeedsYou()
    refreshPulse()
  }, [refreshNeedsYou, refreshPulse, refreshWork])

  const handleSearchChange = value => {
    const next = value == null ? '' : String(value)
    search.setQuery(next)
    setFilters(prev => ({ ...prev, search: next }))
  }

  const handleAdoptionGapChange = nextGap => {
    search.setGap(nextGap || '')
  }

  const handleSelectVenue = venue => {
    if (!venue?.venueId) {
      return
    }
    setDrawerVenue({
      venueId: venue.venueId,
      venueName: venue.venueName || '',
      phoneNumber: venue.phoneNumber || venue.contact?.phone || '',
      contact: venue.contact,
    })
    onDeepLinkVenueChange?.(venue.venueId)
    search.setGap('')
    handleSearchChange('')
  }

  useEffect(() => {
    const venue = getHarbourSoleSearchHit(search)
    if (!venue) {
      if (!String(search.query || '').trim() && !search.gap) {
        autoOpenedSearchRef.current = ''
      }
      return undefined
    }
    const key = `${search.completedGap || ''}:${search.completedQuery}:${
      venue.venueId
    }`
    if (autoOpenedSearchRef.current === key) {
      return undefined
    }
    autoOpenedSearchRef.current = key
    handleSelectVenue(venue)
    return undefined
  }, [
    search.completedGap,
    search.completedQuery,
    search.error,
    search.gap,
    search.loading,
    search.query,
    search.venues,
  ])

  const handleCloseDrawer = () => {
    setDrawerVenue(null)
    onDeepLinkVenueChange?.(null)
  }

  const handleHandoffNudge = (_event, card) => {
    handleSelectVenue(card)
  }

  const handleStartWork = (event, card) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (!card?.venueId || startBusyVenueId) {
      return
    }

    const skip = getHarbourStartWorkSkip(card, account)
    if (skip) {
      toastHarbourStartWorkSkip(skip, card.venueName)
      return
    }

    async function startWork() {
      setStartBusyVenueId(card.venueId)
      try {
        const result = await startHarbourWork({ venueId: card.venueId })
        toastHarbourStartWorkSuccess(result, card.venueName)
        refreshBoards()
      } catch (err) {
        toastHarbourMutationError(err, { venueName: card.venueName })
      } finally {
        setStartBusyVenueId(null)
      }
    }

    startWork()
  }

  const handleOpenSpartacus = query => {
    const fromArg = typeof query === 'string' ? query.trim() : ''
    const next = fromArg || getHarbourSpartacusHandoffQuery(drawerVenue || {})
    // Close here — do not wait for isActive. Do not write ?tab=harbour;
    // the Bridge Spartacus URL is what drops ?venue=.
    setDrawerVenue(null)
    onOpenSpartacus?.(next)
  }

  return (
    <Panel
      data-testid='harbour'
      data-search-miss={search.missed ? 'true' : 'false'}
    >
      <Intro>
        <Title>{HARBOUR_PAGE_TITLE}</Title>
        <Subtitle>{HARBOUR_PAGE_SUBTITLE}</Subtitle>
        <Body>{HARBOUR_INTRO_COPY}</Body>
      </Intro>

      <HarbourHowItWorks accountId={accountId} />

      <HarbourNewsletterReminder accountId={accountId} />

      <HarbourPulse
        pulse={pulse}
        planFilter={filters.planFilter}
        loading={pulseLoading}
        error={pulseError}
        onRetry={refreshPulse}
      />

      <HarbourFilters
        planFilter={filters.planFilter}
        ownerFilter={filters.ownerFilter}
        hideParked={filters.hideParked}
        hasPhone={filters.hasPhone}
        search={search.query}
        adoptionGap={search.gap}
        searchResults={search.venues}
        searchLoading={search.loading}
        onPlanFilterChange={planFilter =>
          applyStickyFilters(prev => ({ ...prev, planFilter }))
        }
        onOwnerFilterChange={ownerFilter =>
          applyStickyFilters(prev => ({ ...prev, ownerFilter }))
        }
        onHideParkedChange={hideParked =>
          applyStickyFilters(prev => ({ ...prev, hideParked }))
        }
        onHasPhoneChange={hasPhone =>
          applyStickyFilters(prev => ({ ...prev, hasPhone }))
        }
        onSearchChange={handleSearchChange}
        onAdoptionGapChange={handleAdoptionGapChange}
        onSelectVenue={handleSelectVenue}
        onOpenSpartacus={handleOpenSpartacus}
        searchMiss={search.missed || search.emptyComplete}
      />

      {search.missed ? (
        <QueueMiss data-testid='harbour-queue-miss'>
          <QueueMissCopy>{HARBOUR_SEARCH_MISS_COPY}</QueueMissCopy>
          {onOpenSpartacus ? (
            <QueueMissButton
              type='button'
              data-testid='harbour-queue-miss-spartacus'
              onClick={() => handleOpenSpartacus(search.query)}
            >
              {HARBOUR_SEARCH_MISS_ACTION}
            </QueueMissButton>
          ) : null}
        </QueueMiss>
      ) : (
        <>
          <HarbourToday
            cards={workBoard.today}
            loading={workLoading}
            error={workError}
            startBusyVenueId={startBusyVenueId}
            onRetry={refreshWork}
            onSelectVenue={handleSelectVenue}
            onStartWork={handleStartWork}
          />

          <HarbourBoard
            needsYouCards={
              needsYouBoard.columns[HARBOUR_COLUMN.NEEDS_YOU] || []
            }
            needsYou={needsYouBoard.needsYou}
            thriving={needsYouBoard.thriving}
            askSales={needsYouBoard.askSales}
            showThriving={showThriving}
            needsYouLoading={needsYouLoading}
            needsYouError={needsYouError}
            workColumns={workBoard.columns}
            workLoading={workLoading}
            workError={workError}
            ownerFilter={filters.ownerFilter}
            hideParked={filters.hideParked}
            startBusyVenueId={startBusyVenueId}
            onRetryNeedsYou={refreshNeedsYou}
            onRetryWork={refreshWork}
            onSelectVenue={handleSelectVenue}
            onStartWork={handleStartWork}
            onAskSales={handleHandoffNudge}
            onAskProduct={handleHandoffNudge}
            onToggleThriving={() => setShowThriving(prev => !prev)}
          />
        </>
      )}

      {isActive && drawerVenue?.venueId ? (
        <HarbourDrawer
          seed={drawerVenue}
          onClose={handleCloseDrawer}
          onOpenSpartacus={handleOpenSpartacus}
          onWorkChanged={refreshBoards}
          onSelectVenue={handleSelectVenue}
        />
      ) : null}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  padding: ${({ theme }) => theme.spacing.sm || '8px'} 0;
`

const Intro = styled.div`
  max-width: 40rem;
`

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm || '8px'};
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Subtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm || '8px'};
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Body = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  line-height: 1.5;
`

const QueueMiss = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  padding: 12px 14px;
  border: 1px solid #d7e3dc;
  border-radius: 8px;
  background: #f4f8f6;
`

const QueueMissCopy = styled.p`
  margin: 0;
  flex: 1 1 16rem;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const QueueMissButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #d7e3dc;
  border-radius: 6px;
  background: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

export default Harbour
